import { ExpandLess, ExpandMore } from '@mui/icons-material';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import { Collapse, Icon, MenuItem, MenuList, Paper, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Toolbar from '@mui/material/Toolbar';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';
import AppBar from './AppBar';
import Drawer from './Drawer';

import { authActions, selectAuthorizationRecord, selectUserName } from '../../auth/store/authSlice';
import { hasUserMenuAccess } from '../../auth/utils/has-user-menu-access.util';
import AppRoutes, { AppRoute } from '../../routing/appRoutes';
import { selectIsDarkMode, settingsActions } from '../../settings/store/settingsSlice';
import { selectCompany } from '../config-loader/store/config-loader-slice';
import useCollapsibleMenu from '../hooks/useCollapsibleMenu';
import { useCompanyLogoConfig } from '../hooks/useCompanyLogoConfig';
import { useAppFeatureWhiteList } from '../hooks/useFeatureWhitelist';
import LanguageSelector from './LanguageSelector';

const AppBarWithDrawerMenu: React.FC<React.PropsWithChildren> = (props) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const { t } = useTranslation();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const company = useSelector(selectCompany);
  const allowedFeatures = useSelector(selectAuthorizationRecord);
  const isDarkMode = useSelector(selectIsDarkMode);

  const { getCompanyAppDrawerLogoConfig, getCompanyLightLogoSrc, getCompanyDarkLogoSrc } = useCompanyLogoConfig(company, isDarkMode);
  const { whiteListedAppRoutes } = useAppFeatureWhiteList(AppRoutes, allowedFeatures);

  const [darkLogoLoaded, setDarkLogoLoaded] = React.useState(false);
  const [lightLogoLoaded, setLightLogoLoaded] = React.useState(false);

  const authorizationRecord = useSelector(selectAuthorizationRecord);
  const userName = useSelector(selectUserName);

  const [drawerOpen, setDrawerOpen] = React.useState(true);

  const { openSubMenu, openSubChildMenu, toggleSubMenu, toggleSubChildMenu, isActiveRoute } = useCollapsibleMenu();

  React.useEffect(() => {
    if (isMobile) {
      setDrawerOpen(false);
    } else {
      setDrawerOpen(true);
    }
  }, [isMobile]);

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleColorThemeToggleButtonClick = () => {
    dispatch(settingsActions.toggleTheme());
  };

  const handleLogoutButtonClick = () => {
    dispatch(authActions.openLogoutDialog());
  };

  const renderSubChildMenu = (menuPath: string, subChildRoutes: AppRoute[]) => {
    return (
      <Collapse in={openSubChildMenu[menuPath]} timeout="auto" unmountOnExit>
        <MenuList>
          {subChildRoutes.map((subChildRoute) =>
            hasUserMenuAccess(subChildRoute.name, authorizationRecord) ? (
              <NavLink
                key={subChildRoute.path}
                to={subChildRoute.redirectTo ? subChildRoute.redirectTo : subChildRoute.path}
                style={{ textDecoration: 'none', color: 'inherit' }}
              >
                <MenuItem sx={{ ml: 7 }} selected={isActiveRoute(subChildRoute.path)}>
                  <ListItemIcon>
                    <Icon>{subChildRoute.iconName || 'label'}</Icon>
                  </ListItemIcon>
                  <ListItemText primary={t(`MENU.${subChildRoute.name}`)} />
                </MenuItem>
              </NavLink>
            ) : (
              false
            )
          )}
        </MenuList>
      </Collapse>
    );
  };

  const renderSubMenu = (menuPath: string, childRoutes: AppRoute[]) => {
    return (
      <Collapse in={openSubMenu[menuPath]} timeout="auto" unmountOnExit>
        <MenuList>
          {childRoutes.map((childRoute) => {
            if (childRoute.children && hasUserMenuAccess(childRoute.name, authorizationRecord)) {
              return (
                <div key={childRoute.path}>
                  <Tooltip title={drawerOpen ? '' : t(`MENU.${childRoute.name}`)} placement="right">
                    <MenuItem sx={{ ml: 2 }} selected={isActiveRoute(childRoute.path)} onClick={() => toggleSubChildMenu(childRoute.path)}>
                      <ListItemIcon>
                        <Icon>{childRoute.iconName || 'label'}</Icon>
                      </ListItemIcon>
                      {drawerOpen && <ListItemText primary={t(`MENU.${childRoute.name}`)} />}
                      {childRoute.isCollapsingMenu && (
                        <ListItemIcon>
                          {!openSubChildMenu[childRoute.path] && <ExpandMore />}
                          {openSubChildMenu[childRoute.path] && <ExpandLess />}
                        </ListItemIcon>
                      )}
                    </MenuItem>
                  </Tooltip>
                  {renderSubChildMenu(childRoute.path, childRoute.children)}
                </div>
              );
            }

            return hasUserMenuAccess(childRoute.name, authorizationRecord) ? (
              <NavLink
                key={childRoute.path}
                to={childRoute.redirectTo ? childRoute.redirectTo : childRoute.path}
                style={{ textDecoration: 'none', color: 'inherit' }}
              >
                <Tooltip title={drawerOpen ? '' : t(`MENU.${childRoute.name}`)} placement="right">
                  <MenuItem sx={{ ml: 2 }} selected={isActiveRoute(childRoute.path)}>
                    <ListItemIcon>
                      <Icon>{childRoute.iconName || 'label'}</Icon>
                    </ListItemIcon>
                    {drawerOpen && <ListItemText primary={t(`MENU.${childRoute.name}`)} />}
                  </MenuItem>
                </Tooltip>
              </NavLink>
            ) : (
              false
            );
          })}
        </MenuList>
      </Collapse>
    );
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar position="absolute">
        <Toolbar
          sx={{
            pr: '24px',
          }}
        >
          <IconButton
            edge="start"
            color="inherit"
            aria-label="open drawer"
            onClick={toggleDrawer}
            sx={{ zIndex: 100, marginRight: '36px' }}
          >
            <MenuIcon />
          </IconButton>
          <Typography component="h1" variant="h6" color="inherit" noWrap sx={{ flexGrow: 1, width: '150px', height: '50px' }}>
            <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
              <img
                style={{
                  ...getCompanyAppDrawerLogoConfig(),
                  position: 'absolute',
                  pointerEvents: 'none',
                  top: 6,
                  left: 50,
                  opacity: isDarkMode && darkLogoLoaded ? 1 : 0,
                  transform: isMobile ? 'scale(0.7)' : 'scale(1)', // Scale down to 70% if mobile
                  transformOrigin: 'top left',
                }}
                src={getCompanyDarkLogoSrc()}
                alt="Gallery Dark Logo"
                onLoad={() => setDarkLogoLoaded(true)}
              />
              <img
                style={{
                  ...getCompanyAppDrawerLogoConfig(),
                  position: 'absolute',
                  pointerEvents: 'none',
                  top: 6,
                  left: 50,
                  opacity: !isDarkMode && lightLogoLoaded ? 1 : 0,
                  transform: isMobile ? 'scale(0.7)' : 'scale(1)', // Scale down to 70% if mobile
                  transformOrigin: 'top left',
                }}
                src={getCompanyLightLogoSrc()}
                alt="Gallery Light Logo"
                onLoad={() => setLightLogoLoaded(true)}
              />
            </div>
          </Typography>

          <Box sx={{ marginRight: '16px' }}>{userName}</Box>
          <LanguageSelector />

          <IconButton sx={{ ml: 1 }} onClick={handleColorThemeToggleButtonClick} color="inherit">
            {theme.palette.mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
          </IconButton>

          <IconButton sx={{ ml: 1 }} onClick={handleLogoutButtonClick} color="inherit">
            <LogoutIcon fontSize="small" />
          </IconButton>
        </Toolbar>
      </AppBar>
      <Drawer
        variant="permanent"
        open={drawerOpen}
        sx={{
          width: drawerOpen ? '210px' : '80px',
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: drawerOpen ? '210px' : '80px',
            boxSizing: 'border-box',
            backgroundColor: theme.palette.mode === 'light' ? '#F7FAFB' : 'inherit',
          },
        }}
      >
        <Toolbar
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            px: [1],
          }}
        >
          <IconButton onClick={toggleDrawer}>
            <ChevronLeftIcon />
          </IconButton>
        </Toolbar>
        <Divider />
        <List component="nav" sx={{ width: '100%' }}>
          <MenuList>
            {whiteListedAppRoutes.map((appRoute: AppRoute) => {
              if (appRoute.children && hasUserMenuAccess(appRoute.name, authorizationRecord)) {
                return (
                  <div key={appRoute.name}>
                    <Tooltip title={drawerOpen ? '' : t(`MENU.${appRoute.name}`)} placement="right">
                      <MenuItem onClick={() => toggleSubMenu(appRoute.path)}>
                        <ListItemIcon>
                          <Icon>{appRoute.iconName || 'label'}</Icon>
                        </ListItemIcon>
                        {drawerOpen && <ListItemText primary={t(`MENU.${appRoute.name}`)} />}
                        {appRoute.isCollapsingMenu && (
                          <ListItemIcon>
                            {!openSubMenu[appRoute.path] && <ExpandMore />}
                            {openSubMenu[appRoute.path] && <ExpandLess />}
                          </ListItemIcon>
                        )}
                      </MenuItem>
                    </Tooltip>
                    {renderSubMenu(appRoute.path, appRoute.children)}
                  </div>
                );
              }

              return hasUserMenuAccess(appRoute.name, authorizationRecord) ? (
                <NavLink
                  key={appRoute.path}
                  to={appRoute.redirectTo ? appRoute.redirectTo : appRoute.path}
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                  <Tooltip title={drawerOpen ? '' : t(`MENU.${appRoute.name}`)} placement="right">
                    <MenuItem selected={isActiveRoute(appRoute.path)}>
                      <ListItemIcon>
                        <Icon>{appRoute.iconName || 'label'}</Icon>
                      </ListItemIcon>
                      {drawerOpen && <ListItemText primary={t(`MENU.${appRoute.name}`)} />}
                    </MenuItem>
                  </Tooltip>
                </NavLink>
              ) : (
                false
              );
            })}
          </MenuList>
        </List>
      </Drawer>
      <Paper
        component="main"
        sx={{
          flexGrow: 1,
          elevation: 0,
          boxShadow: 0,
          height: '100vh',
          overflow: 'auto',
        }}
      >
        <Toolbar />
        {props.children}
      </Paper>
    </Box>
  );
};

export default AppBarWithDrawerMenu;
